import React, { useState } from 'react'
import { Box, Chip, Tooltip, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IAlarm } from '../use-sips'
import moment from 'moment'

import AlarmForm from './AlarmForm'

const useAlarmTooltip = (alarm?: IAlarm, isRecurring?: boolean) => {
  const { t, i18n } = useTranslation()
  if (!alarm) return ''
  const time = alarm.alarm_time
  const fmt = (date: string) => moment(date).locale(i18n.language).format('L')
  if (isRecurring) {
    return t('voip.sip.alarm.tooltip_recurring', {
      start: fmt(alarm.start_date),
      end: fmt(alarm.end_date),
      time,
    })
  }
  return t('voip.sip.alarm.tooltip_once', {
    date: fmt(alarm.start_date),
    time,
  })
}

const AlarmChip = ({ alarm, room_number }: { alarm?: IAlarm, room_number: string }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [openForm, setOpenForm] = useState(false)
  const isRecurring = alarm && moment(alarm.end_date).diff(moment(alarm.start_date), 'days') > 0
  const tooltip = useAlarmTooltip(alarm, !!isRecurring)

  if (!alarm || alarm.status === 'expired') {
    return (
      <>
        <Tooltip title={t('voip.sip.alarm.tooltip_create')} arrow placement="left">
          <Chip
            size="small"
            icon={
              <i
                className="fa-solid fa-alarm-plus"
                style={{ fontSize: 12, fontWeight: 900, color: theme.palette.grey[800], marginLeft: '2px', marginRight: '2px' }}
              />
            }
            label=""
            variant="outlined"
            onClick={() => setOpenForm(true)}
            sx={theme => ({
              cursor: 'pointer',
              borderColor: theme.palette.grey[100],
              borderRadius: '8px',
              height: 28,
              padding: '0 4px',
              '& .MuiChip-label': { display: 'none' },
              '& .MuiChip-icon': { margin: 0 }
            })}
          />
        </Tooltip>
        {openForm && (
          <AlarmForm
            room_numbers={[room_number]}
            onClose={() => setOpenForm(false)}
            onSubmit={() => setOpenForm(false)}
          />
        )}
      </>
    )
  }
  if (isRecurring) {
    return (
      <>
        <Tooltip title={tooltip} arrow placement="left">
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Chip
              size="small"
              label={alarm.alarm_time}
              icon={
                <i
                  className="fa-solid fa-alarm-clock"
                  style={{ fontSize: 12, fontWeight: 900, marginLeft: '6px', color: theme.palette.grey[800] }}
                />
              }
              variant="outlined"
              onClick={() => setOpenForm(true)}
              sx={theme => ({
                cursor: 'pointer',
                borderColor: theme.palette.grey[100],
                borderRadius: '8px',
                color: theme.palette.grey[700],
                height: 28,
                fontSize: 12,
                fontWeight: 500
              })}
            />
            <Box
              sx={theme => ({
                position: 'absolute',
                top: -6,
                right: -6,
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.grey[800],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 8
              })}>
              <i className="fa-solid fa-arrows-repeat" />
            </Box>
          </Box>
        </Tooltip>
        {openForm && (
          <AlarmForm
            room_numbers={[room_number]}
            start_date={alarm.start_date}
            end_date={alarm.end_date}
            alarm_time={alarm.alarm_time}
            timezone={alarm.timezone}
            onClose={() => setOpenForm(false)}
            onSubmit={() => setOpenForm(false)}
          />
        )}
      </>
    )
  }
  return (
    <>
      <Tooltip title={tooltip} arrow placement="left">
        <Chip
          size='small'
          label={alarm.alarm_time}
          onClick={() => setOpenForm(true)}
          icon={<i className="fa-solid fa-alarm-clock" style={{ fontSize: 12, fontWeight: 900, marginLeft: '6px' }} />}
          variant='outlined'
          sx={(theme) => ({
            borderColor: theme.palette.grey[100],
            borderRadius: '8px',
            color: theme.palette.grey[700],
            height: 28,
            fontSize: 12,
            fontWeight: 500,
          })}
        />
      </Tooltip>
      {
        openForm && (
          <AlarmForm
            room_numbers={[room_number]}
            start_date={alarm.start_date}
            end_date={alarm.end_date}
            alarm_time={alarm.alarm_time}
            timezone={alarm.timezone}
            onClose={() => setOpenForm(false)}
            onSubmit={() => setOpenForm(false)}
          />
        )
      }
    </>
  )
}

export default AlarmChip